import React, { useEffect, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { Box } from "@mui/material";
import { COLORS, Loading } from "components";
import { useMutation } from "@tanstack/react-query";
import { updateVarianceConsumption, updateVarianceEmissions, updateVarianceFinancials } from "api";
import { chartColors, commonStyles } from "../const";
import { VarianceChartProps } from "../types";

export const VarianceChart = ({
  chartTitle,
  searchFilters,
  subTabName,
  startDate,
  endDate,
  currentMainTab
}: VarianceChartProps) => {
  const [varianceData, setVarianceData] = useState<any>([]);
  const apiFn = currentMainTab === "GHG Emissions" ? updateVarianceEmissions : currentMainTab === "Consumption" ? updateVarianceConsumption : updateVarianceFinancials;
  const { mutateAsync: updateVariance, isLoading } = useMutation(apiFn);
  const { total, totalLastYear } = varianceData;
  const noDataAvailable = !isLoading && Object.values(varianceData).every(value =>
    typeof value === "object" && value !== null && Object.keys(value).length === 0
  );

  const isEmissionsChart = subTabName === "location" || subTabName === "market";

  const currentData: any[] = total ? Object.values(total).flat() : [];
  const lastYearData: any[] = totalLastYear ? Object.values(totalLastYear).flat() : [];

  const totalCategories = currentData.map((entry: any) => entry.month + entry.year.replace("20", " '"));

  const currentYearTotalVariance = currentData.map((entry: any) => isEmissionsChart ? entry[`${subTabName}BasedEmission`] : entry.consumption ?? entry.amount).map(variance => variance === null ? 0 : variance);
  const lastYearTotalVariance = lastYearData.map((entry: any) => isEmissionsChart ? entry[`${subTabName}BasedEmission`] : entry.consumption ?? entry.amount).map(variance => variance === null ? 0 : variance);

  const unitYAxis = isEmissionsChart ? "tCO2e" : currentData[0]?.unit ?? lastYearData[0]?.unit ?? currentData[0]?.currency ?? lastYearData[0]?.currency ?? "No Data Available";

  useEffect(() => {
    const fetchVarianceData = async () => {
      setVarianceData({});
      await updateVariance({
        ...searchFilters,
        compareFromDate: startDate.toISOString().substring(0, 10),
        compareToDate: endDate.toISOString().substring(0, 10),
      }, {
        onSuccess: (response: any) => {
          setVarianceData(response.data);
        },
        onError: () => {
          // implement error handling
        },
      })
    };

    fetchVarianceData();
  }, [searchFilters, startDate, endDate, updateVariance])

  const chartOptions = {
    chart: {
      spacing: [30, 50, 20, 40],
      width: null,
    },
    title: {
      text: `${chartTitle}${noDataAvailable ? ": No Data Available" : ''}`,
      align: "left",
      margin: 40,
      style: {
        ...commonStyles,
        color: COLORS.Mainblue,
        fontSize: "20px",
      },
    },
    xAxis: {
      categories: totalCategories,
      labels: {
        style: commonStyles,
      },
      startOnTick: false,
      endOnTick: false,
    },
    yAxis: {
      title: {
        text: unitYAxis,
        style: commonStyles,
        offset: 50
      },
      labels: {
        style: commonStyles,
      },
    },
    series: [
      {
        type: "area",
        name: "Current Period",
        data: currentYearTotalVariance,
        color: chartColors[0],
        fillOpacity: 0.3,
        lineWidth: 6,
        marker: { enabled: false },
      },
      {
        type: "area",
        name: "Same Period Last Year",
        data: lastYearTotalVariance,
        color: chartColors[1],
        fillOpacity: 0.3,
        lineWidth: 6,
        marker: { enabled: false, symbol: "circle" },
      },
    ],
    legend: {
      title: {
        style: commonStyles,
      },
      align: "left",
      verticalAlign: "bottom",
      x: 20,
      margin: 30,
    },
    tooltip: {
      shared: false,
      borderRadius: 27,
      backgroundColor: COLORS.Mainblue,
      shadow: false,
      padding: 10,
      style: {
        ...commonStyles,
        color: "white",
        fontSize: "12px",
      },
      formatter(this: Highcharts.TooltipFormatterContextObject) {
        return `<b>${Number(this!.y?.toFixed(2)).toLocaleString('en-US')} ${unitYAxis}</b>`;
      }
    },
    credits: {
      enabled: false,
    },
  };


  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
        fontFamily: "Inter",
        "& > div": {
          borderRadius: 2,
        },
      }}
    >
      {isLoading && <Box sx={{ height: 300, position: "absolute", zIndex: 99999, right: "50%" }}>
        <Loading />
      </Box>}
      {/* Chart */}
      <HighchartsReact highcharts={Highcharts} options={chartOptions} />
    </Box>
  );
};
